import { init } from '@embedpdf/pdfium';
import wasmUrl from '@embedpdf/pdfium/pdfium.wasm?url';

let pendingEngine;
async function loadEngine() {
  if (!pendingEngine) pendingEngine = (async () => {
    let wasmBinary;
    if (import.meta.env.MODE === 'test') {
      const { readFile } = await import('node:fs/promises');
      wasmBinary = await readFile(new URL('../../node_modules/@embedpdf/pdfium/dist/pdfium.wasm', import.meta.url));
    } else {
      const response = await fetch(wasmUrl);
      if (!response.ok) throw new Error('The local PDF editing engine could not be loaded. Please reopen the app.');
      wasmBinary = new Uint8Array(await response.arrayBuffer());
    }
    const engine = await init({ wasmBinary });
    engine.PDFiumExt_Init();
    return engine;
  })().catch(error => { pendingEngine = null; throw error; });
  return pendingEngine;
}

// Each invocation runs synchronously after initialization. No document handles
// are shared across requests and no asynchronous operation can interleave calls
// into the same WASM instance. Save workers each own their separate instance.
async function editWithPdfium(input, edit) {
  const api = await loadEngine();
  const heap = api.pdfium;
  const inputPtr = heap._malloc(input.length);
  if (!inputPtr) throw new Error('Not enough memory to edit this PDF.');
  heap.HEAPU8.set(input, inputPtr);
  const doc = api.FPDF_LoadMemDocument(inputPtr, input.length, '');
  if (!doc) { heap._free(inputPtr); throw new Error('The PDF editing engine could not open this document.'); }
  try {
    edit(api, heap, doc);
    const writer = api.PDFiumExt_OpenFileWriter();
    try {
      // Full save, never incremental: removed content must not remain in an
      // earlier PDF revision. Reachability cleanup runs in the caller as well.
      api.PDFiumExt_SaveAsCopy(doc, writer);
      const size = api.PDFiumExt_GetFileWriterSize(writer);
      if (!size) throw new Error('The PDF editing engine produced no output.');
      const ptr = heap._malloc(size);
      if (!ptr) throw new Error('Not enough memory to save this PDF.');
      try {
        api.PDFiumExt_GetFileWriterData(writer, ptr, size);
        return heap.HEAPU8.slice(ptr, ptr + size);
      } finally { heap._free(ptr); }
    } finally { api.PDFiumExt_CloseFileWriter(writer); }
  } finally { api.FPDF_CloseDocument(doc); heap._free(inputPtr); }
}

export function rewriteWithPdfium(input, regions, onProgress = () => {}) {
  return editWithPdfium(input, (api, heap, doc) => {
    for (const [index, region] of regions.entries()) {
      const page = api.FPDF_LoadPage(doc, region.pageNum - 1);
      if (!page) throw new Error('The PDF editing engine could not open this page.');
      const rect = heap._malloc(16);
      try {
        if (!rect) throw new Error('Not enough memory to edit this PDF.');
        const [left, bottom, right, top] = region.rect;
        // The pinned engine shifts the text matrix after deleting a leading
        // run. A retained zero-ink space can then move the hit-testing frame
        // for later letters. Select from the original page first and remove
        // glyphs from the end of the run, so that frame cannot skip a suffix.
        const textPage = api.FPDFText_LoadPage(page);
        if (!textPage) throw new Error('The source text could not be inspected.');
        const boxes = heap._malloc(32);
        const glyphs = [];
        const objects = new Map();
        try {
          if (!boxes) throw new Error('Not enough memory to inspect source text.');
          for (let i = api.FPDFText_CountChars(textPage) - 1; i >= 0; i--) {
            if (!api.FPDFText_GetCharBox(textPage, i, boxes, boxes + 8, boxes + 16, boxes + 24)) continue;
            const l = heap.getValue(boxes, 'double'), r = heap.getValue(boxes + 8, 'double');
            const b = heap.getValue(boxes + 16, 'double'), t = heap.getValue(boxes + 24, 'double');
            const object = api.FPDFText_GetTextObject(textPage, i);
            const byOrigin = region.kind === 'text' && Number.isFinite(region.baseline);
            let selected = r > l && t > b && l < right && r > left && b < top && t > bottom;
            if (byOrigin) {
              if (!api.FPDFText_GetCharOrigin(textPage, i, boxes, boxes + 8)) throw new Error('The source text position could not be inspected.');
              const x = heap.getValue(boxes, 'double'), y = heap.getValue(boxes + 8, 'double');
              selected = Math.abs(y - region.baseline) < .01 && x >= left - .01 && x < right - .01;
            }
            if (object) {
              const state = objects.get(object) || { allSelected: true, selected: false };
              state.allSelected &&= selected; state.selected ||= selected; objects.set(object, state);
            }
            if (selected && r > l && t > b) {
              glyphs.push({ object, rect: byOrigin ? [l, t, r, b] : [Math.max(l, left), Math.min(t, top), Math.min(r, right), Math.max(b, bottom)] });
            }
          }
        } finally { if (boxes) heap._free(boxes); api.FPDFText_ClosePage(textPage); }
        const removed = new Set();
        const removeWholeRuns = (parent, form = false) => {
          const count = form ? api.FPDFFormObj_CountObjects(parent) : api.FPDFPage_CountObjects(parent);
          for (let i = count - 1; i >= 0; i--) {
            const object = form ? api.FPDFFormObj_GetObject(parent, i) : api.FPDFPage_GetObject(parent, i);
            const state = objects.get(object);
            if (state?.allSelected && state.selected) {
              const ok = form ? api.FPDFFormObj_RemoveObject(parent, object) : api.FPDFPage_RemoveObject(parent, object);
              if (!ok) throw new Error('The selected text could not be removed.');
              removed.add(object); api.FPDFPageObj_Destroy(object);
            } else if (api.FPDFPageObj_GetType(object) === 5) removeWholeRuns(object, true);
          }
        };
        removeWholeRuns(page);
        for (const glyph of glyphs) {
          if (removed.has(glyph.object)) continue;
          glyph.rect.forEach((value, i) => heap.setValue(rect + i * 4, value, 'float'));
          api.EPDFText_RedactInRect(page, rect, true, false);
        }
        [left, top, right, bottom].forEach((value, i) => heap.setValue(rect + i * 4, value, 'float'));
        if (region.kind === 'text') {
          // False also means there was no overlapping glyph (already deleted).
          if (!Number.isFinite(region.baseline)) api.EPDFText_RedactInRect(page, rect, true, false);
        } else {
          const annotation = api.FPDFPage_CreateAnnot(page, 28);
          if (!annotation) throw new Error('The redaction could not be created.');
          try {
            if (!api.FPDFAnnot_SetRect(annotation, rect) || !api.EPDFAnnot_ApplyRedaction(page, annotation)) {
              throw new Error('The redaction could not be applied. No changes were saved.');
            }
          } finally { api.FPDFPage_CloseAnnot(annotation); }
        }
        if (!api.FPDFPage_GenerateContent(page)) throw new Error('The edited page could not be saved.');
      } finally { if (rect) heap._free(rect); api.FPDF_ClosePage(page); }
      onProgress((index + 1) / (regions.length + 1));
    }
  });
}

export function generateMissingAppearances(input, targets) {
  return editWithPdfium(input, (api, _heap, doc) => {
    for (const { pageIndex, annotationIndex, widget } of targets) {
      const page = api.FPDF_LoadPage(doc, pageIndex);
      if (!page) throw new Error('The PDF page could not be opened.');
      const annot = api.FPDFPage_GetAnnot(page, annotationIndex);
      try {
        if (!annot || !(widget ? api.EPDFAnnot_GenerateFormFieldAP(annot) : api.EPDFAnnot_GenerateAppearance(annot))) {
          throw new Error('An annotation has no appearance that can be flattened. Save a regular copy to preserve it.');
        }
      } finally { if (annot) api.FPDFPage_CloseAnnot(annot); api.FPDF_ClosePage(page); }
    }
  });
}
